import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_HOME = [
  { label: 'proyecto', hash: '#proyecto' },
  { label: 'modelos', hash: '#modelos' },
  { label: 'master plan', hash: '#disponibilidad' },
  { label: 'amenidades', hash: '#amenidades' },
  { label: 'ubicación', hash: '#ubicacion' },
  { label: 'tour 360', hash: '#recorrido-virtual' },
  { label: 'detalles', hash: '#hecho-a-mano' },
];

const NAV_FERNANDA = [
  { label: 'proyecto', hash: '#proyecto' },
  { label: 'tour 360', hash: '#recorrido-vr' },
  { label: 'detalles', hash: '#hecho-a-mano' },
  { label: 'FAQs', hash: '#faq' },
  { label: 'Modelo Mariana', path: '/modelo-mariana' },
];

const NAV_MARIANA = [
  { label: 'proyecto', hash: '#proyecto' },
  { label: 'tour 360', hash: '#recorrido-vr' },
  { label: 'detalles', hash: '#hecho-a-mano' },
  { label: 'FAQs', hash: '#faq' },
  { label: 'Modelo Fernanda', path: '/modelo-fernanda' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const isFernanda = location.pathname === '/modelo-fernanda';
  const isMariana  = location.pathname === '/modelo-mariana';
  const isHome     = location.pathname === '/';

  const navLinks = isFernanda ? NAV_FERNANDA : isMariana ? NAV_MARIANA : NAV_HOME;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track which section is currently visible to highlight the corresponding nav item.
  useEffect(() => {
    setActiveHash('');
    const hashTargets = navLinks
      .filter(l => l.hash)
      .map(l => ({ hash: l.hash, el: document.querySelector(l.hash) }))
      .filter(t => t.el);
    if (hashTargets.length === 0) return;

    const visibility = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        visibility.set(e.target, e.isIntersecting ? (e.intersectionRatio || 0.001) : 0);
      });
      // Pick the most visible section as active
      let best = { hash: '', ratio: 0 };
      hashTargets.forEach((t) => {
        const r = visibility.get(t.el) || 0;
        if (r > best.ratio) best = { hash: t.hash, ratio: r };
      });
      setActiveHash(best.ratio > 0 ? best.hash : '');
    }, {
      // Activa la sección cuyo contenido cruce la zona alta del viewport
      rootMargin: '-90px 0px -60% 0px',
      threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
    });
    hashTargets.forEach(t => io.observe(t.el));
    return () => io.disconnect();
  }, [navLinks, location.pathname]);

  const handleNavLink = (e, item) => {
    e.preventDefault();
    setMobileOpen(false);
    if (item.path) {
      navigate(item.path);
      return;
    }
    if (isHome || isFernanda || isMariana) {
      const el = document.querySelector(item.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + item.hash);
    }
  };

  return (
    <>
      <nav
        className={`main-nav-component${scrolled ? ' is-scrolled' : ''}${(isHome || isFernanda || isMariana) ? ' on-home' : ''}`}
      >
        <div className="page-padding">
          <div className="container-large">
            <div className="primary-nav-bar">
              <Link to="/" className="brand" onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.history.replaceState(null, '', '/');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // Let route change, GlobalUI will scroll to top
                }
              }}>
                <img loading="eager" fetchpriority="high" className="brand-logo brand-logo-dark"
                  src="/images/santa-monica-residencial-dark.svg"
                  alt="Santa Mónica Residencial" />
                <img loading="eager" fetchpriority="high" className="brand-logo brand-logo-white"
                  src="/images/santa-monica-residencial-white.svg"
                  alt="Santa Mónica Residencial" />
              </Link>

              {/* Desktop nav */}
              <div className="nav-links">
                {navLinks.map(link => {
                  const isActive = link.path
                    ? location.pathname === link.path
                    : (link.hash && link.hash === activeHash);
                  return (
                    <a
                      key={link.label}
                      href={link.hash || link.path}
                      className={`btn-nav${isActive ? ' active' : ''}`}
                      onClick={(e) => handleNavLink(e, link)}
                    >
                      <span className="btn-nav-label">{link.label}</span>
                    </a>
                  );
                })}
              </div>

              {/* CTA button */}
              <div className="nav-cta">
                <button
                  type="button"
                  className="btn-pill btn-pill-nav"
                  data-testid="nav-agendar-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('sm:open-agendar'))}
                >
                  Agenda tu visita
                  <span className="icon-font">event</span>
                </button>
              </div>

              {/* Hamburger */}
              <button className="brgr-btn" onClick={() => setMobileOpen(true)} aria-label="Menú">
                <div className="brgr-pleca" />
                <div className="brgr-pleca" />
                <div className="brgr-pleca" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile nav overlay */}
      {mobileOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileOpen(false)}>
          <div className="mobile-nav-panel" onClick={e => e.stopPropagation()}>
            <div className="mobile-nav-header">
              <button className="mobile-close-btn" onClick={() => setMobileOpen(false)}>
                <span className="icon-font">close</span>
              </button>
            </div>
            <div className="mobile-nav-body">
              {navLinks.map(link => (
                <a
                  key={link.label}
                  href={link.hash || link.path}
                  className="btn-modal-nav"
                  onClick={(e) => handleNavLink(e, link)}
                >
                  {link.label}
                </a>
              ))}
              <button
                type="button"
                className="btn-pill"
                style={{ marginTop: '1.5rem', alignSelf: 'center' }}
                onClick={() => {
                  setMobileOpen(false);
                  window.dispatchEvent(new CustomEvent('sm:open-agendar'));
                }}
                data-testid="mobile-nav-agendar"
              >
                Agenda tu visita
                <span className="icon-font">event</span>
              </button>
              <div className="flex-el" style={{ marginTop: '1.5rem' }}>
                <a href="https://www.facebook.com/SantaMonicaResidencialMx" target="_blank" rel="noopener noreferrer" className="icon-social-btn" aria-label="Facebook">
                  <img loading="lazy" src="/images/facebook.svg" alt="" />
                </a>
                <a href="https://www.instagram.com/santamonica_mx/" target="_blank" rel="noopener noreferrer" className="icon-social-btn" aria-label="Instagram">
                  <img loading="lazy" src="/images/instagram.svg" alt="" />
                </a>
                <a href="https://www.youtube.com/@quru5366" target="_blank" rel="noopener noreferrer" className="icon-social-btn youtube" aria-label="YouTube">
                  <img loading="lazy" src="/images/youtube.svg" alt="" />
                </a>
              </div>
            </div>
            <div className="mobile-nav-footer">Santa Mónica Residencial</div>
          </div>
        </div>
      )}
    </>
  );
}
